import React, { useEffect } from "react";
import "./PublishedResearch.css";

const PublishedResearch = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="research-page-container">
        <div className="research-header">
          <h1>FROM FARMERS’ TRUST TO PUBLISHED AGRICULTURAL RESEARCH</h1>
          <p className="subtitle">Four YK Innovations. Three Important Crops. Four Proud Research Milestones.</p>
        </div>

        <div className="research-intro">
          <p>YK Laboratories did not begin its journey inside a boardroom.</p>
          <p>Our journey began in farmers’ fields—observing crop problems, understanding plant behaviour, developing formulations, conducting repeated trials and standing beside farmers until visible results appeared.</p>
          <p>Today, that long journey has reached another important stage.</p>
          <p>Four YK Laboratories products have been evaluated under field conditions and their research findings have been published in agricultural journals:</p>
          
          <ul className="product-highlights">
            <li>🌶️ <strong>AROGYA®️</strong> for Chilli</li>
            <li>🍅 <strong>MAX-SIZE®️</strong> for Tomato</li>
            <li>🌱 <strong>OXYVEG®️</strong> for Cotton</li>
            <li>🌶️ <strong>YKSTAR®️</strong> for Chilli</li>
          </ul>

          <p>These are not simply four product names.</p>
          <p>They represent years of observation, formulation development, field experience, farmer confidence and continuous commitment to scientific agriculture.</p>
        </div>

        <hr className="section-divider" />

        <div className="research-cards-container">
          
          {/* Card 1: AROGYA */}
          <div className="research-card">
            <div className="card-header arogya">
              <h2>1. AROGYA®️ — PUBLISHED RESEARCH IN CHILLI</h2>
            </div>
            <div className="card-body">
              <p>AROGYA®️ was evaluated in chilli under field conditions using different application doses.</p>
              <p><strong>AROGYA®️ at 3 mL/L recorded the highest values:</strong></p>
              <ul className="results-list">
                <li>Fruit length: 11.95 cm</li>
                <li>Fruit width: 0.85 cm</li>
                <li>Fruits per plant: 142.50</li>
                <li>Fruit yield per plant: 428 g</li>
                <li>Chilli yield: 118.89 quintals per hectare</li>
                <li>AROGYA®️ 2.5 mL/L was statistically at par and was identified as the best effective lower dose</li>
                <li>No phytotoxicity was observed under the trial conditions</li>
              </ul>
              
              <div className="publication-details">
                <h4>Publication details:</h4>
                <p><strong>Research title:</strong> “Evaluation of Bio-efficacy of Bio-stimulant AROGYA on Chilli (Capsicum annuum L.)”</p>
                <p><strong>Published in:</strong> International Journal of Horticulture and Food Science</p>
                <p>Volume 7, Issue 8, pages 89–92</p>
                <p><strong>DOI:</strong> <a href="https://doi.org/10.33545/26631067.2025.v7.i8b.370" target="_blank" rel="noreferrer">10.33545/26631067.2025.v7.i8b.370</a></p>
                <button className="read-research-btn" onClick={() => window.open("https://doi.org/10.33545/26631067.2025.v7.i8b.370")}>Read the published AROGYA®️ research</button>
              </div>
            </div>
          </div>

          {/* Card 2: MAX-SIZE */}
          <div className="research-card">
            <div className="card-header maxsize">
              <h2>2. MAX-SIZE®️ — PUBLISHED RESEARCH IN TOMATO</h2>
            </div>
            <div className="card-body">
              <p>MAX-SIZE®️ was evaluated for its effect on tomato growth, fruit development, yield and quality.</p>
              <p><strong>MAX-SIZE®️ at 7.5 mL/L recorded the highest values:</strong></p>
              <ul className="results-list">
                <li>Plant height: 93.40 cm</li>
                <li>Stem girth: 2.50 cm</li>
                <li>Fruits per plant: 83</li>
                <li>Fruit length: 5.20 cm</li>
                <li>Fruit diameter: 6.70 cm</li>
                <li>Individual fruit weight: 88.20 g</li>
                <li>Yield per plant: 1.45 kg</li>
                <li>Tomato yield: 536.50 quintals per hectare</li>
                <li>Shelf life: 16.40 days</li>
                <li>MAX-SIZE®️ 5 mL/L produced statistically comparable results for most parameters and was identified as the best effective lower dose</li>
              </ul>
              
              <div className="publication-details">
                <h4>Publication details:</h4>
                <p><strong>Research title:</strong> “Assessing the Effect of Bio Stimulant on Growth, Yield and Quality of Tomato”</p>
                <p><strong>Published in:</strong> Journal of Advances in Biology & Biotechnology</p>
                <p>Volume 28, Issue 6, pages 850–855</p>
                <p><strong>DOI:</strong> <a href="https://doi.org/10.9734/jabb/2025/v28i62446" target="_blank" rel="noreferrer">10.9734/jabb/2025/v28i62446</a></p>
                <button className="read-research-btn" onClick={() => window.open("https://doi.org/10.9734/jabb/2025/v28i62446")}>Read the published MAX-SIZE®️ research</button>
              </div>
            </div>
          </div>

          {/* Card 3: OXYVEG */}
          <div className="research-card">
            <div className="card-header oxyveg">
              <h2>3. OXYVEG®️ — PUBLISHED RESEARCH IN COTTON</h2>
            </div>
            <div className="card-body">
              <p>OXYVEG®️ was evaluated under field conditions at the Agriculture Research Station, Achalpur, under Dr. Panjabrao Deshmukh Krishi Vidyapeeth, Akola.</p>
              <p><strong>OXYVEG®️ at 3 mL/L recorded:</strong></p>
              <ul className="results-list">
                <li>Highest seed-cotton yield: 1,500.76 kg per hectare</li>
                <li>Plant height: 108.05 cm</li>
                <li>Sympodial branches: 6.70 per plant</li>
                <li>Yield per plant: 30.17 g</li>
                <li>Available nitrogen: 198.20 kg/ha</li>
                <li>Available phosphorus: 23.48 kg/ha</li>
                <li>Available potassium: 460.21 kg/ha</li>
                <li>OXYVEG®️ 1.5 mL/L recorded 1,414.63 kg/ha and was statistically at par with 3 mL/L</li>
              </ul>
              
              <div className="publication-details">
                <h4>Publication details:</h4>
                <p><strong>Research title:</strong> “Efficacy Evaluation of Bio-stimulant OXYVEG on Growth and Yield of Cotton”</p>
                <p><strong>Published in:</strong> Journal of Advances in Biology & Biotechnology</p>
                <p>Volume 28, Issue 8, pages 783–787</p>
                <p><strong>DOI:</strong> <a href="https://doi.org/10.9734/jabb/2025/v28i82752" target="_blank" rel="noreferrer">10.9734/jabb/2025/v28i82752</a></p>
                <button className="read-research-btn" onClick={() => window.open("https://doi.org/10.9734/jabb/2025/v28i82752")}>Read the published OXYVEG®️ research</button>
              </div>
            </div>
          </div>

          {/* Card 4: YKSTAR */}
          <div className="research-card">
            <div className="card-header ykstar">
              <h2>4. YKSTAR®️ — PUBLISHED RESEARCH IN CHILLI</h2>
            </div>
            <div className="card-body">
              <p>YKSTAR®️ was evaluated in chilli at different application doses under field conditions.</p>
              <p><strong>YKSTAR®️ at 3 mL/L recorded the highest values:</strong></p>
              <ul className="results-list">
                <li>Fruit length: 11.90 cm</li>
                <li>Fruit width: 0.84 cm</li>
                <li>Fruits per plant: 141.60</li>
                <li>Fruit yield per plant: 420 g</li>
                <li>Chilli yield: 116.66 quintals per hectare</li>
              </ul>
              
              <p><strong>YKSTAR®️ at 2 mL/L recorded:</strong></p>
              <ul className="results-list">
                <li>Fruits per plant: 138</li>
                <li>Fruit yield per plant: 410 g</li>
                <li>Chilli yield: 113.89 quintals per hectare</li>
                <li>Untreated control yield: 77.22 quintals per hectare</li>
                <li>Approximately 47.5% higher yield than the untreated control</li>
                <li>The 2 mL/L and 3 mL/L treatments were statistically at par; therefore, 2 mL/L was identified as the best effective lower dose</li>
                <li>No phytotoxicity was observed under the trial conditions</li>
              </ul>
              
              <div className="publication-details">
                <h4>Publication details:</h4>
                <p><strong>Research title:</strong> “Evaluation of Bio-efficacy of Bio-stimulant YK STAR on Chilli”</p>
                <p><strong>Published in:</strong> International Journal of Horticulture and Food Science</p>
                <p>Volume 8, Issue 1, pages 7–10</p>
                <p><strong>DOI:</strong> <a href="https://doi.org/10.33545/26631067.2026.v8.i1a.448" target="_blank" rel="noreferrer">10.33545/26631067.2026.v8.i1a.448</a></p>
                <button className="read-research-btn" onClick={() => window.open("https://doi.org/10.33545/26631067.2026.v8.i1a.448")}>Read the published YKSTAR®️ research</button>
              </div>
            </div>
          </div>

        </div>

        <hr className="section-divider" />

        <div className="research-conclusion">
          <h2>THIS IS MORE THAN FOUR PUBLICATIONS</h2>
          
          <div className="transformation-box">
            <p>These achievements represent a proud transformation:</p>
            <ul>
              <li>From field observations to measured results.</li>
              <li>From farmer confidence to published evidence.</li>
              <li>From Indian formulations to scientific agricultural recognition.</li>
            </ul>
          </div>
          
          <p className="highlight-text">
            Our published research now covers:<br/>
            Two YK products for chilli.<br/>
            One YK product for cotton.<br/>
            One YK product for tomato.
          </p>

          <p>This gives YK Laboratories a strong research foundation across three commercially important Indian crops.</p>

          <div className="responsibility-box">
            <h3>Every result increases our confidence—but it also increases our responsibility.</h3>
            <p>We must continue to:</p>
            <ul>
              <li>Maintain consistent product quality</li>
              <li>Communicate only documented findings</li>
              <li>Guide farmers with the correct crop, dose and application stage</li>
              <li>Continue university and field research</li>
              <li>Convert scientific knowledge into practical farmer benefits</li>
            </ul>
          </div>

          <p className="thank-you-text">We sincerely thank the agricultural scientists, research teams, field staff, distributors and farmers whose contribution and trust made this journey possible.</p>
          <p className="thank-you-text">This achievement belongs to every person who believed that an Indian agricultural company could combine traditional botanical knowledge, modern formulation development and structured field research.</p>
        </div>

        <div className="mission-banner">
          <h2>FOUR YK PRODUCTS</h2>
          <h2>THREE IMPORTANT CROPS</h2>
          <h2>FOUR PUBLISHED RESEARCH MILESTONES</h2>
          <h2 className="main-mission">ONE CONTINUING MISSION—BETTER AGRICULTURE FOR FARMERS</h2>
          
          <div className="product-summary">
            <span>AROGYA®️ — Chilli</span> | 
            <span> MAX-SIZE®️ — Tomato</span> | 
            <span> OXYVEG®️ — Cotton</span> | 
            <span> YKSTAR®️ — Chilli</span>
          </div>
          
          <div className="slogan">
            SCIENCE-BACKED. FARMER-TRUSTED. FUTURE-READY.
          </div>
          
          <div className="company-signoff">
            <h3>YK Laboratories – Herbal House</h3>
            <p>Research • Innovation • Agriculture</p>
          </div>
        </div>

        <p className="disclaimer">
          <em>The reported figures represent results obtained under the respective study conditions. Crop performance may vary according to variety, climate, soil, crop management and application practices.</em>
        </p>
      </div>
    </>
  );
};

export default PublishedResearch;
